import {get, reduce} from 'lodash';
import { getBasicConfig$Cache } from "@/common/service/common";
import {
  ON_SESSION_PAYMENT_SUPPORTED_FLOWS,
  UPI_PAYMENT_METHOD_UNSUPPORTED_FLOWS,
  UPI_PAYMENT_METHOD_UNSUPPORTED_PLAN_TYPES,
  ZUORA_OUTAGE_ALTER_CC_SUPPORTED_FLOWS,
  THEME,
  UPI_MAX_MANDATE_AMOUNT,
  DEFAULT_KAKAO_PAY_MAX_AMOUNT,
  DEFAULT_MOMO_PAY_MAX_AMOUNT,
  DEFAULT_GCASH_MAX_AMOUNT,
} from '@/views/checkout/constant';
import { payMethodMap } from '@/views/checkout/payment/helper/constant';
import { pageConfig } from '@/common/service/page-config';
import { isCurrency, isInrCurrency } from '@/utils/currency';
import { checkIfPaidToPaidFlow } from '@/views/checkout/payment/helper/util';
import {
  COUNTRY_CODE_KOREA,
  COUNTRY_CODE_PHILIPPINES,
  COUNTRY_CODE_VIETNAM,
  CURRENCY_KRW,
  CURRENCY_PHP,
  CURRENCY_VND,
} from '@/common/constants';

import messageMonitorForZVA from '@/utils/message-monitor-for-ZVA.js'
import { isDesktopDevice } from '@/utils/payment';

const getDefaultIpLocation = () => {
  return {
    cn: "",
    countryName: "",
    region: "",
    regionName: "",
    city: "",
    postalCode: "",
    locationId: undefined,
  }
}

const disabledCurrencyFilterTypeMap = {
  categoryCode: 1,
  planCode: 2,
}

// disabled parent categoryCode or planCode with currency list
const transferDisabledCurrencyList = (disableCurrencyList) => {
  return reduce(disableCurrencyList, (res, item) => {
    const codeType = item?.filterType;

    if (codeType === disabledCurrencyFilterTypeMap.categoryCode) {
      res.disableCurrency4PlanCategory[item?.code] = item?.currencyList;
    } else if (codeType === disabledCurrencyFilterTypeMap.planCode) {
      res.disableCurrency4PlanCode[item?.code] = item?.currencyList;
    }

    return res;
  }, {
    disableCurrency4PlanCategory: {},
    disableCurrency4PlanCode: {},
  })
}

export const COMMON_ACTION_TYPES = {
  basicConfig: '/basic/config',
  initPage: '/init/page',
}

const state = {
  basicConfigReady: false,
  clientWebViewTheme: THEME.LIGHT,
  isCollapseSimplifyView: true,
  isSimplifyCompoundOrderView: false,
  ipLocation: getDefaultIpLocation(),
  ttrTaxExemptionEnabled: false,
  enableProMonIncrease: false,
  enableProAnnualIncrease: false,
  enableTranslation: false,
  indiaMandateAmount: '',
  indiaMaxMandateAmount: '',
  indiaMandateFactor: '',
  onSessionPaymentEnabled: false,
  inrCitEnabled: false, // feature flag
  disableCurrency4PlanCategory: {},
  enableArlCheckbox: false,
  arlStateList:[],
  enableBizPriceIncrease: false,
  globalErrorState: false,
  showInrNewTableContent: false,
  upiPaymentEnabled: false,
  upiPaymentGateway: '',
  upiAsOneTimePayment: false,
  eduDomainList: [],
  govDomainList: [],
  enableServerNextChargeDateLogic: false,
  enableServerNextChargeDateLogicPhase2: false,
  disableExpireCredits: false,
  // ALTERNATIVE CREDIT CARD FOR ZUORA OUTAGE
  isZuoraOutage: false,
  // Disable Payment
  disablePayment: window && window.SB && window.SB.disablePayment,
  isConfigCardLoading: false,
  lnProfile: {
    domain: null,
    orgId: null,
    sessionId: null
  },
  disableBusinessVerification: false,
  japanIP: false,
  kakaoPayPaymentEnabled: false,
  kakaoPayPaymentGateway: '',
  kakaoPayMaxTransactionAmount: 0,
  momoPayPaymentEnabled: false,
  enableMomoPayMobile: false,
  momoPayPaymentGateway: '',
  momoPayMaxTransactionAmount: 0,
  gCashPaymentEnabled: false,
  gCashPaymentGateway: '',
  gCashMaxTransactionAmount: 0,
  secondTokenMaxPollingInterval: 0,
  enableApplePayNonSafari: false,
  enablePreferredPaymentByCountry: false,
  enableSDKFreeTrial: false,
  enableBuildPlatform: false,
  enableAdyenGatewayAvailable: true,
  enablePayPalGatewayAvailable: true,
  enablePaypalCommercePlatformGateway: false,
  enableSignInModal: false,
  enableWebinar300: false,
  enableOnlineRemoveWebinar10000: false,
  enableHardwareHeadset4OnlinePh2: false,
  learnMoreConfig: {
    visible: false,
    modalType: null,
    planType: null,
    categoryCode: null,
    productPlans: [],
    hideOptions: false,
    addAction: () => {},
    trackingBasePlanName: null,
    trackingPlanTitle: null
  }
}

const getters = {
  basicConfigReady: (state) => state.basicConfigReady,
  clientWebViewTheme: (state) => state.clientWebViewTheme,
  isCollapseSimplifyView: (state) => state.isCollapseSimplifyView,
  isSimplifyCompoundOrderView: (state) => state.isSimplifyCompoundOrderView,
  ipLocation: (state) => state.ipLocation,
  ttrTaxExemptionEnabled: (state) => state.ttrTaxExemptionEnabled,
  enableProMonIncrease: (state) => state.enableProMonIncrease,
  enableProAnnualIncrease: (state) => state.enableProAnnualIncrease,
  enableTranslation: (state) => state.enableTranslation,
  enableArlCheckbox: (state) => state.enableArlCheckbox,
  enableSDKFreeTrial: (state) => state.enableSDKFreeTrial,
  enableBuildPlatform: (state) => state.enableBuildPlatform,
  arlStateList:(state) => state.arlStateList,
  flowType: (state)=>state.flowType,
  disableCurrency4PlanCategory: (state) => state.disableCurrency4PlanCategory,
  indiaMandateAmount: (state) => state.indiaMandateAmount,
  indiaMaxMandateAmount: (state) => state.indiaMaxMandateAmount,
  indiaMinMandateAmount: (state) => state.indiaMinMandateAmount,
  indiaMandateFactor: (state) => state.indiaMandateFactor,
  onSessionPaymentEnabled: (state) => state.onSessionPaymentEnabled,
  enableBizPriceIncrease: (state) => state.enableBizPriceIncrease,
  enableHardwareHeadset4OnlinePh2: state => state.enableHardwareHeadset4OnlinePh2,
  inrCitEnabled: (state) => state.inrCitEnabled,
  globalErrorState: (state) => state.globalErrorState,
  showInrNewTableContent: (state) => state.showInrNewTableContent,
  upiPaymentEnabled: (state, getters) => {
    const { getSelectedPlans, basicConfig, isInrCitEligible} = getters;
    if (!state.upiPaymentEnabled) {
      return false;
    }
    // Disable UPI for Direct Sales customers
    if (basicConfig?.onlineSupportedAccount === false) {
      return false;
    }
    // Check UPI P2P flag
    if (checkIfPaidToPaidFlow(window.$context.flow) && !basicConfig?.upiPaymentEnabledForP2P) {
      return false;
    }
    // Disable UPI for P2P MIT flow
    if (checkIfPaidToPaidFlow(window.$context.flow) && !isInrCitEligible) {
      return false;
    }
    // Disable UPI for VideoSDK plan when it's the only plan in the cart
    if(getSelectedPlans?.length === 1 && UPI_PAYMENT_METHOD_UNSUPPORTED_PLAN_TYPES.indexOf(getSelectedPlans[0]?.type) !== -1) {
      return false;
    }
    // Disable UPI for BuyNow when total amount > 15k
    if (!checkIfPaidToPaidFlow(window.$context.flow)) {
      const { getTotalCharge, getTaxTotalAmount } = getters;
      const totalChargeToday = (getTotalCharge + getTaxTotalAmount) / 100;
      if (totalChargeToday >= UPI_MAX_MANDATE_AMOUNT) {
        return false;
      }
    }
    return true;
  },
  upiPaymentGateway: (state) => state.upiPaymentGateway,
  upiAsOneTimePayment: (state, getters) => {
    const { selectedPaymentMethod } = getters;
    if (!state.upiAsOneTimePayment || selectedPaymentMethod !== payMethodMap.upi) {
      return false;
    }

    // in case of INR CIT use pre-calculated amount
    const { isInrCitEligible, isProratedChargeAmountCalculated } = getters;
    if (isInrCitEligible && isProratedChargeAmountCalculated) {
      const { getProratedChargeTotalAmount } = getters;
      return (getProratedChargeTotalAmount / 100) >= UPI_MAX_MANDATE_AMOUNT;
    }

    // Check that total amount is greater than 15k
    const { getTotalCharge, getTaxTotalAmount } = getters;
    const totalChargeToday = (getTotalCharge + getTaxTotalAmount) / 100;
    return totalChargeToday >= UPI_MAX_MANDATE_AMOUNT;
  },
  // ALTERNATIVE CREDIT CARD FOR ZUORA OUTAGE
  isZuoraOutage: (state) => state.isZuoraOutage,
  // disablePayment
  disablePayment: (state) => state.disablePayment,
  alternativeCreditCardEnabled: (state) => {
    if(!state.isZuoraOutage) {
      return false;
    }
    return ZUORA_OUTAGE_ALTER_CC_SUPPORTED_FLOWS.includes(pageConfig.type);
  },
  isConfigCardLoading: (state) => state.isConfigCardLoading,
  eduDomainList: state => state.eduDomainList,
  eduDomainRegList: state => state.eduDomainList.map(domain => {
    if (domain.indexOf('$') !== -1) {
      return new RegExp(domain, 'i');
    } else {
      return new RegExp(`${domain.replace(/(?=\.)/g, '\\')}$`, 'i');
    }
  }),
  govDomainList: state => state.govDomainList,
  govDomainRegList: state => state.govDomainList.map(domain => {
    if (domain.indexOf('$') !== -1) {
      return new RegExp(domain, 'i');
    } else {
      return new RegExp(`${domain.replace(/(?=\.)/g, '\\')}$`, 'i');
    }
  }),
  lnProfile: (state) => state.lnProfile, // LexisNexis ThreatMetrix profile data
  enableServerNextChargeDateLogic: state => state.enableServerNextChargeDateLogic,
  enableServerNextChargeDateLogicPhase2: state => state.enableServerNextChargeDateLogicPhase2,
  disableExpireCredits: state => state.disableExpireCredits,
  disableBusinessVerification: state => state.disableBusinessVerification,
  kakaoPayMaxTransactionAmount: state => state.kakaoPayMaxTransactionAmount,
  kakaoPayPaymentEnabled: (state, getters) => {
    if (window.$config('isMasterOrSubAccount')) return false;
    const { basicConfig, billToContact, kakaoPayMaxTransactionAmount } = getters;
    if (basicConfig?.onlineSupportedAccount === false) {
      return false;
    }
    if (checkIfPaidToPaidFlow(window.$context.flow) && !basicConfig?.kakaoPayPaymentEnabledForP2P) {
      return false;
    }
    const { getTotalCharge, getTaxTotalAmount } = getters;
    const totalChargeToday = (getTotalCharge + getTaxTotalAmount) / 100;
    if (totalChargeToday >= kakaoPayMaxTransactionAmount) {
      return false;
    }
    if (billToContact?.country?.toUpperCase() !== COUNTRY_CODE_KOREA) {
      return false;
    }
    return state.kakaoPayPaymentEnabled;
  },
  kakaoPayPaymentGateway: (state) => state.kakaoPayPaymentGateway,
  momoPayMaxTransactionAmount: state => state.momoPayMaxTransactionAmount,
  momoPayPaymentEnabled: (state, getters) => {
    if (window.$config('isMasterOrSubAccount')) return false;
    const { basicConfig, billToContact, momoPayMaxTransactionAmount } = getters;
    if (basicConfig?.onlineSupportedAccount === false) {
      return false;
    }
    if (checkIfPaidToPaidFlow(window.$context.flow) && !basicConfig?.momoPayPaymentEnabledForP2P) {
      return false;
    }
    const { getTotalCharge, getTaxTotalAmount } = getters;
    const totalChargeToday = (getTotalCharge + getTaxTotalAmount) / 100;
    if (totalChargeToday >= momoPayMaxTransactionAmount) {
      return false;
    }
    if (billToContact?.country?.toUpperCase() !== COUNTRY_CODE_VIETNAM) {
      return false;
    }
    // ZOOM-1035749 Check feature flag for MoMo on mobile devices
    if (!isDesktopDevice() && !state.enableMomoPayMobile) {
      return false;
    }
    return state.momoPayPaymentEnabled;
  },
  momoPayPaymentGateway: (state) => state.momoPayPaymentGateway,
  gCashMaxTransactionAmount: state => state.gCashMaxTransactionAmount,
  gCashPaymentEnabled: (state, getters) => {
    if (window.$config('isMasterOrSubAccount')) return false;
    const { basicConfig, billToContact, gCashMaxTransactionAmount } = getters;
    if (basicConfig?.onlineSupportedAccount === false) {
      return false;
    }
    if (checkIfPaidToPaidFlow(window.$context.flow) && !basicConfig?.gCashPaymentEnabledForP2P) {
      return false;
    }
    const { getTotalCharge, getTaxTotalAmount } = getters;
    const totalChargeToday = (getTotalCharge + getTaxTotalAmount) / 100;
    if (totalChargeToday >= gCashMaxTransactionAmount) {
      return false;
    }
    if (billToContact?.country?.toUpperCase() !== COUNTRY_CODE_PHILIPPINES) {
      return false;
    }
    return state.gCashPaymentEnabled;
  },
  gCashPaymentGateway: (state) => state.gCashPaymentGateway,
  isEWalletPayment: (state, getters) => {
    const { selectedPaymentMethod } = getters;
    return (
      selectedPaymentMethod === payMethodMap.kakaoPay ||
      selectedPaymentMethod === payMethodMap.momoPay ||
      selectedPaymentMethod === payMethodMap.gCash
    );
  },
  secondTokenMaxPollingInterval: (state) => state.secondTokenMaxPollingInterval,
  enableApplePayNonSafari: (state) => state.enableApplePayNonSafari,
  enablePreferredPaymentByCountry: (state) => state.enablePreferredPaymentByCountry,
  enablePassProductInfoToZVA: (state) => state.enablePassProductInfoToZVA,
  enableAdyenGatewayAvailable: (state) => state.enableAdyenGatewayAvailable,
  enablePayPalGatewayAvailable: (state) => state.enablePayPalGatewayAvailable,
  enablePaypalCommercePlatformGateway: (state) => state.enablePaypalCommercePlatformGateway,
  enableWebinar300: (state) => state.enableWebinar300,
  enableOnlineRemoveWebinar10000: (state) => state.enableOnlineRemoveWebinar10000,
  learnMoreConfig: (state) => state.learnMoreConfig
}

const mutations = {
  setClientWebViewTheme (state, value) {
    state.clientWebViewTheme = value;
  },
  updateIsCollapseSimplifyView(state, value) {
    state.isCollapseSimplifyView = value;
  },
  updateIsSimplifyCompoundOrderView(state, value) {
    state.isSimplifyCompoundOrderView = value;
  },
  setIpLocation(state, data) {
    state.ipLocation = data;
  },
  setTtrTaxExemptionEnabled(state, data) {
    state.ttrTaxExemptionEnabled = data;
  },
  setEnableProMonIncrease(state, isEnable) {
    state.enableProMonIncrease = !!isEnable;
  },
  setDisableCurrency4PlanCategory(state, data = {}) {
    state.disableCurrency4PlanCategory = data;
  },
  setEnableBizPriceIncrease(state, isEnable) {
    state.enableBizPriceIncrease = !!isEnable;
  },
  setOnSessionPaymentEnabled(state, isEnable) {
    state.onSessionPaymentEnabled = !!isEnable;
  },
  setGlobalErrorState(state, data) {
    state.globalErrorState = data;
  },
  // ALTERNATIVE CREDIT CARD FOR ZUORA OUTAGE
  setZuoraOutage(state, isOutage) {
    state.isZuoraOutage = !!isOutage;
  },
  // Disable Payment
  setPaymentDisable(state, isDisabled) {
    state.disablePayment = !!isDisabled;
  },
  setConfigCardLoading(state, isLoading) {
    state.isConfigCardLoading = !!isLoading;
  },
  setEduDomainList(state, domainList) {
    state.eduDomainList = domainList;
  },
  setGovDomainList(state, domainList) {
    state.govDomainList = domainList
  },
  setEnablePreferredPaymentByCountry(state, isEnable) { // currently called via Optimizely (ZTSPZ-604)
    state.enablePreferredPaymentByCountry = isEnable;
  },
  setLearnMoreConfig(state, data) {
    state.learnMoreConfig = data;
  },
  setEnableHardwareHeadset4OnlinePh2(state, isEnable) {
    state.enableHardwareHeadset4OnlinePh2 = isEnable;
  }
}

const actions = {
  async [COMMON_ACTION_TYPES.initPage]({dispatch}) {
    try {
      await Promise.all([dispatch(COMMON_ACTION_TYPES.basicConfig), dispatch("getBasicConfig")]);
    } catch (e) {
      console.error(e);
    }
  },
  async [COMMON_ACTION_TYPES.basicConfig]({state, commit}) {
    const {result = getDefaultIpLocation()} = await getBasicConfig$Cache();
    const {
      iplocation,
      disableCurrencyList = [],
      ttrTaxExemptionEnabled = false,
      enableProMonIncrease = false,
      enableProAnnualIncrease = false,
      enableTranslation = false,
      isIndiaCustomerNonINR = false,
      enableCycleLimit = false,
      indiaMandateAmount = '',
      indiaMaxMandateAmount = '',
      indiaMinMandateAmount = '',
      indiaMandateFactor =  '',
      onSessionPaymentEnabled = false,
      inrCitEnabled = false,
      leadFormSupportPlanCodes,
      enableBizPriceIncrease = false,
      enableHardwareHeadset4OnlinePh2 = false,
      showInrNewTableContent = false,
      upiPaymentEnabled = false,
      upiPaymentGateway = '',
      upiAsOneTimePayment = false,
      eduDomainList = [],
      govDomainList = [],
      lnProfile = {},
      enableServerNextChargeDateLogic = false,
      enableServerNextChargeDateLogicPhase2 = false,
      disableExpireCredits = false,
      disableBusinessVerification = false,
      japanIP = false,
      kakaoPayPaymentEnabled = false,
      kakaoPayPaymentGateway = '',
      kakaoPayMaxTransactionAmount = 0,
      momoPayPaymentEnabled = false,
      enableMomoPayMobile = false,
      momoPayPaymentGateway = '',
      momoPayMaxTransactionAmount = 0,
      gCashPaymentEnabled = false,
      gCashPaymentGateway = '',
      gCashMaxTransactionAmount = 0,
      secondTokenMaxPollingInterval = 0,
      enableArlCheckbox = false,
      enableSDKFreeTrial = false,
      enableBuildPlatform = false,
      arlStateList = [],
      enableApplePayNonSafari = false,
      enablePassProductInfoToZVA = false,
      enableAdyenGatewayAvailable = true,
      enablePayPalGatewayAvailable = true,
      enablePaypalCommercePlatformGateway = false,
      enableSignInModal = false,
      enableWebinar300 = false,
      enableOnlineRemoveWebinar10000 = false,
    } = result;
    const { disableCurrency4PlanCategory } = transferDisabledCurrencyList(disableCurrencyList);

    commit('setIpLocation', iplocation);
    commit('setTtrTaxExemptionEnabled', ttrTaxExemptionEnabled);
    commit('setDisableCurrency4PlanCategory', disableCurrency4PlanCategory);
    commit('setLeadCreationSupportedPlans', leadFormSupportPlanCodes);
    commit('setEduDomainList', eduDomainList);
    commit('setGovDomainList', govDomainList);
    // update flag
    state.enableProMonIncrease = enableProMonIncrease;
    state.enableProAnnualIncrease = enableProAnnualIncrease;
    state.enableTranslation = enableTranslation;
    state.isIndiaCustomerNonINR = isIndiaCustomerNonINR;
    state.enableCycleLimit = enableCycleLimit;
    state.indiaMandateAmount = indiaMandateAmount;
    state.indiaMaxMandateAmount = indiaMaxMandateAmount;
    state.indiaMinMandateAmount = indiaMinMandateAmount;
    state.indiaMandateFactor = indiaMandateFactor;
    state.onSessionPaymentEnabled = onSessionPaymentEnabled && isInrCurrency() && ON_SESSION_PAYMENT_SUPPORTED_FLOWS.includes(pageConfig.type);
    state.inrCitEnabled = inrCitEnabled && isInrCurrency();
    state.enableBizPriceIncrease = enableBizPriceIncrease;
    state.showInrNewTableContent = showInrNewTableContent;
    state.enableHardwareHeadset4OnlinePh2 = enableHardwareHeadset4OnlinePh2;
    state.upiPaymentGateway = upiPaymentGateway;
    state.upiAsOneTimePayment = upiAsOneTimePayment;
    state.lnProfile = lnProfile;
    state.enableServerNextChargeDateLogic = enableServerNextChargeDateLogic;
    state.enableServerNextChargeDateLogicPhase2 = enableServerNextChargeDateLogicPhase2;
    state.disableExpireCredits = disableExpireCredits;
    state.disableBusinessVerification = disableBusinessVerification;
    state.japanIP = japanIP;
    state.enableArlCheckbox = enableArlCheckbox;
    state.enableSDKFreeTrial = enableSDKFreeTrial;
    state.enableBuildPlatform = enableBuildPlatform;
    state.arlStateList = arlStateList;
    state.enableSignInModal = enableSignInModal;

    state.upiPaymentEnabled = upiPaymentEnabled && isInrCurrency() && !UPI_PAYMENT_METHOD_UNSUPPORTED_FLOWS.includes(pageConfig.type);

    state.basicConfigReady = true

    const showKakaoPayPayment = get(window.campaignFlags, 'showKakaoPayPayment', false);
    state.kakaoPayPaymentEnabled = isCurrency(CURRENCY_KRW) && kakaoPayPaymentEnabled && showKakaoPayPayment;
    state.kakaoPayPaymentGateway = kakaoPayPaymentGateway;
    state.kakaoPayMaxTransactionAmount = kakaoPayMaxTransactionAmount || DEFAULT_KAKAO_PAY_MAX_AMOUNT;

    const showMomoPayPayment = get(window.campaignFlags, 'showMomoPayPayment', false);
    state.momoPayPaymentEnabled = isCurrency(CURRENCY_VND) && momoPayPaymentEnabled && showMomoPayPayment;
    state.enableMomoPayMobile = enableMomoPayMobile;
    state.momoPayPaymentGateway = momoPayPaymentGateway;
    state.momoPayMaxTransactionAmount = momoPayMaxTransactionAmount || DEFAULT_MOMO_PAY_MAX_AMOUNT;

    const showGCashPayment = get(window.campaignFlags, 'showGCashPayment', false);
    state.gCashPaymentEnabled = isCurrency(CURRENCY_PHP) && gCashPaymentEnabled && showGCashPayment;
    state.gCashPaymentGateway = gCashPaymentGateway;
    state.gCashMaxTransactionAmount = gCashMaxTransactionAmount || DEFAULT_GCASH_MAX_AMOUNT;

    state.secondTokenMaxPollingInterval = secondTokenMaxPollingInterval;

    state.enableApplePayNonSafari = enableApplePayNonSafari;

    state.enablePassProductInfoToZVA = enablePassProductInfoToZVA;
    if (enablePassProductInfoToZVA) {
      messageMonitorForZVA.init(window.__$app__?.$store?.getters?.account?.email || window.SB?.user?.email || '');
    }

    state.enableAdyenGatewayAvailable = enableAdyenGatewayAvailable
    state.enablePayPalGatewayAvailable = enablePayPalGatewayAvailable
    state.enablePaypalCommercePlatformGateway = enablePaypalCommercePlatformGateway

    state.enableWebinar300 = enableWebinar300
    state.enableOnlineRemoveWebinar10000 = enableOnlineRemoveWebinar10000
  }

}

export default {
  state,
  getters,
  mutations,
  actions,
}
